import { OverridedMixpanel } from "mixpanel-browser";
import httpClient from "../../clients/httpClient";
import {
    IDateRange,
    IEventRecordsQuery,
} from "../../interfaces/analytics.interface";

const toParams = (range: IDateRange) => ({ start: range.start, end: range.end });

export const getDashboardSummary = (range: IDateRange) =>
    httpClient.get(`/api/v1/admin/analytics/dashboard/summary`, {
        params: toParams(range),
    });

export const getDashboardTenants = (range: IDateRange, limit = 50) =>
    httpClient.get(`/api/v1/admin/analytics/dashboard/tenants`, {
        params: { ...toParams(range), limit },
    });

export const getDashboardEvents = (range: IDateRange, limit = 20) =>
    httpClient.get(`/api/v1/admin/analytics/dashboard/events`, {
        params: { ...toParams(range), limit },
    });

export const getDashboardTrends = (range: IDateRange) =>
    httpClient.get(`/api/v1/admin/analytics/dashboard/trends`, {
        params: toParams(range),
    });

export const getTenantSummary = (tenantId: string, range: IDateRange) =>
    httpClient.get(
        `/api/v1/admin/analytics/dashboard/tenant/${tenantId}/summary`,
        { params: toParams(range) }
    );

export const getTenantEvents = (tenantId: string, range: IDateRange, limit = 20) =>
    httpClient.get(
        `/api/v1/admin/analytics/dashboard/tenant/${tenantId}/events`,
        { params: { ...toParams(range), limit } }
    );

export const getTenantTrends = (tenantId: string, range: IDateRange) =>
    httpClient.get(
        `/api/v1/admin/analytics/dashboard/tenant/${tenantId}/trends`,
        { params: toParams(range) }
    );

/**
 * Raw events for one event name, global or scoped to a tenant.
 *
 * Snapshot pagination: the first request of a query (event, tenant, start,
 * end) sends no `as_of`. The server cuts the count and the rows at its request
 * time and returns that instant as `pagination.as_of`. Every later page,
 * Previous and Retry of the same query sends it back as `as_of`, so all pages
 * share one total and events that arrive meanwhile never shift rows between
 * pages. A new query (other event, tenant or range) starts again without it.
 *
 * Residual, not solved here: Firehose delivers events in buffers (~5 min), so
 * an event with event_timestamp <= snapshot that lands after the first page was
 * served can still shift later pages slightly.
 */
export const getEventRecords = ({
    tenantId,
    eventName,
    range,
    page,
    perPage,
    asOf,
}: IEventRecordsQuery) =>
    httpClient.get(
        tenantId
            ? `/api/v1/admin/analytics/dashboard/tenant/${encodeURIComponent(tenantId)}/events/records`
            : `/api/v1/admin/analytics/dashboard/events/records`,
        {
            params: {
                ...toParams(range),
                event_name: eventName,
                page,
                per_page: perPage,
                ...(asOf ? { as_of: asOf } : {}),
            },
        }
    );

export const sendAnalytics = (event: Record<string, any>) =>
    httpClient.post(`/api/v1/analytics`, event);

export const sendToAnalyticsAndMixpanel = (tenantId: number, webAuthToken: string, storeId: number, eventName: string, data: Record<string, any>, mixpanel?: OverridedMixpanel) => {
    return Promise.all([
        mixpanel ? mixpanel.track(eventName, data) : Promise.resolve(),
        sendAnalytics({
            ...data,
            'Event Name': eventName,
            tenant_id: tenantId,
            web_auth_token: webAuthToken,
            'Store ID': storeId,
            platform: 'web'
        })
    ])
}